export type MemoFn<T> = (invalidation?: (data: T) => void, forceUpdate?: boolean) => Promise<T>;

/**
 * Memoizes an async request, caching the result for a specified TTL.
 * After TTL expires, returns stale result immediately and refreshes in background.
 *
 * @param callback - The async function to memoize.
 * @param ttl - Cache lifetime in seconds. Defaults to 900 s.
 */
export function memoRequest<T>(callback: () => Promise<T>, ttl = 900): MemoFn<T> {
  const state: { promise?: Promise<T>; result?: T; ttl?: number } = {};

  return async (invalidation, forceUpdate = false) =>
    new Promise<T>(async (resolve, reject) => {
      const now = new Date().getTime();

      if (state.result !== undefined && !forceUpdate) {
        resolve(state.result);
        if (state.ttl! > now) {
          return;
        }

        // Return current result and refresh in background due to expiration
        resolve = reject = undefined as any;
      }

      try {
        if (!state.promise || state.ttl! <= now) {
          state.promise = callback();
        }
        state.result = await state.promise;
        state.ttl = now + ttl * 1000;

        (resolve ?? invalidation)?.(state.result);
      } catch (error) {
        reject?.(error as Error);
      }
    });
}
